export const COMPLIANCE_QUESTIONNAIRES = {
  WA_AFH_072_FIRE_EXTINGUISHERS: {
    label: "Fire Extinguishers",
    reference: "WAC 388-76-10720, WAC 388-76-10725",
    description:
      "Confirm extinguisher coverage, rating, placement, and monthly/annual inspection status.",
    icon: "ti-fire-extinguisher",
    questions: [
      {
        key: "everyFloorHasOne",
        prompt: "Does every floor have a fire extinguisher?",
        type: "boolean",
        required: true,
      },
      {
        key: "meetsMinimumRating",
        prompt:
          "Do all extinguishers meet the minimum 2A:10B:C or 5 lb ABC rating?",
        type: "boolean",
        required: true,
      },
      {
        key: "properlyMounted",
        prompt:
          "Are all extinguishers wall-mounted, visible, and unobstructed?",
        type: "boolean",
        required: true,
      },
      {
        key: "coversKitchen",
        prompt: "Is there an extinguisher near the kitchen/cooking area?",
        type: "boolean",
        required: true,
      },
      {
        key: "kitchenDistanceFeet",
        prompt: "How far is it from the cooking equipment (feet)?",
        type: "number",
        showIf: { key: "coversKitchen", equals: true },
        required: true,
      },
      {
        key: "monthlyCheckDate",
        prompt: "When was the last monthly visual check?",
        type: "date",
        required: true,
      },
      {
        key: "annualInspectionDate",
        prompt: "When was the last annual professional inspection?",
        type: "date",
        required: true,
      },
    ],
  },

  WA_AFH_076_DOORS_ABILITY_TO_OPEN: {
    label: "Doors",
    reference: "WAC 388-76-10715",
    description:
      "Track door hardware, locking mechanisms, accessibility, and width compliance.",
    icon: "ti-door",
    questions: [
      {
        key: "bedroomBathroomDoorsOpenFreely",
        prompt:
          "Do all bedroom and bathroom doors open freely from both inside and outside?",
        type: "boolean",
        required: true,
      },
      {
        key: "locksUnlockableFromOutside",
        prompt:
          "Can all locked bedroom/bathroom doors be unlocked from outside without a key or tool in an emergency?",
        type: "boolean",
        required: true,
      },
      {
        key: "closetDoorsOpenBothSides",
        prompt: "Do all closet doors open from both inside and outside?",
        type: "boolean",
        required: true,
      },
      {
        key: "hasOneDesignatedExit",
        prompt:
          "Is exactly one exterior door designated as the emergency exit?",
        type: "boolean",
        required: true,
      },
      {
        key: "exitHasLeverHandlesAndReentry",
        prompt:
          "Does that exit have lever handles on both sides and allow exit/re-entry without a key?",
        type: "boolean",
        required: true,
      },
      {
        key: "otherExteriorDoorsAccessible",
        prompt:
          "Are all other exterior doors freely accessible, or is a documented safety exemption on file for any that aren't?",
        type: "boolean",
        required: true,
      },
      {
        key: "allDoorsMeetWidthMinimum",
        prompt: "Do all resident-used doors meet the minimum 27-inch width?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_077_WINDOWS: {
    label: "Windows",
    reference: "WAC 388-76-10795",
    description:
      "Confirm bedroom windows meet sill height, opening size, and emergency egress requirements.",
    icon: "ti-window",
    questions: [
      {
        key: "everyBedroomHasQualifyingWindow",
        prompt:
          "Does every bedroom have at least one window meeting sill height (\u226444in), opening size (\u22655.7 sq ft), and opening dimension requirements?",
        type: "boolean",
        required: true,
      },
      {
        key: "windowsOpenableWithoutTools",
        prompt:
          "Can all qualifying windows be opened from inside without keys, tools, or special effort?",
        type: "boolean",
        required: true,
      },
      {
        key: "hasStormWindows",
        prompt: "Are any bedroom windows fitted with storm windows?",
        type: "boolean",
      },
      {
        key: "stormWindowsHaveReleaseMechanism",
        prompt:
          "Do those storm windows have a release mechanism that preserves emergency egress?",
        type: "boolean",
        showIf: { key: "hasStormWindows", equals: true },
        required: true,
      },
      {
        key: "hasBasementWindows",
        prompt: "Does the home have any basement bedroom windows?",
        type: "boolean",
      },
      {
        key: "basementWindowsUnobstructed",
        prompt:
          "Are those basement windows free from obstructions blocking emergency escape or rescue?",
        type: "boolean",
        showIf: { key: "hasBasementWindows", equals: true },
        required: true,
      },
    ],
  },

  WA_AFH_071_SMOKE_ALARMS: {
    label: "Smoke Alarms",
    reference: "WAC 388-76-10805",
    description:
      "Confirm smoke alarm coverage, interconnection, working condition, and testing/battery schedule throughout the home.",
    icon: "ti-alarm",
    questions: [
      {
        key: "everyBedroomHasAlarm",
        prompt: "Does every resident bedroom have a smoke alarm?",
        type: "boolean",
        required: true,
      },
      {
        key: "everyFloorHasAlarm",
        prompt: "Does every floor level have smoke alarm coverage?",
        type: "boolean",
        required: true,
      },
      {
        key: "alarmsInterconnectedOrWireless",
        prompt:
          "Are all alarms interconnected (or wireless with all sounding together) so one activating triggers all?",
        type: "boolean",
        required: true,
      },
      {
        key: "allAlarmsWorking",
        prompt: "Are all smoke alarms currently in working condition?",
        type: "boolean",
        required: true,
      },
      {
        key: "lastOperationalTestDate",
        prompt:
          "When was the last monthly operational test (audible alert/test button) performed on all alarms?",
        type: "date",
        required: true,
      },
      {
        key: "usesSealed10YearBatteries",
        prompt:
          "Are all smoke alarms sealed, non-removable 10-year battery units?",
        type: "boolean",
        required: true,
      },
      {
        key: "lastBatteryReplacementDate",
        prompt: "When were batteries last replaced?",
        type: "date",
        showIf: { key: "usesSealed10YearBatteries", equals: false },
        required: true,
      },
    ],
  },

  WA_AFH_080_SPACE_HEATERS_FIREPLACES_STOVES: {
    label: "Space Heaters, Fireplaces & Stoves",
    reference: "WAC 388-76-10825",
    description:
      "Confirm space heater certification, escape-route clearance, and barriers on hot-to-touch surfaces.",
    icon: "ti-flame",
    questions: [
      {
        key: "hasSpaceHeaters",
        prompt:
          "Does the home use any oil, gas, kerosene, or electric space heaters?",
        type: "boolean",
      },
      {
        key: "heatersAreLabCertified",
        prompt:
          "Are those space heaters certified by a nationally recognized testing laboratory?",
        type: "boolean",
        showIf: { key: "hasSpaceHeaters", equals: true },
        required: true,
      },
      {
        key: "dontBlockEscapeRoutes",
        prompt:
          "Do all stoves and heaters stay clear of resident, staff, and household escape routes?",
        type: "boolean",
        required: true,
      },
      {
        key: "hasHotToTouchAppliances",
        prompt:
          "Do any fireplaces, stoves, or heaters get hot to the touch when in use?",
        type: "boolean",
      },
      {
        key: "hotSurfacesHaveBarriers",
        prompt:
          "Do those hot-to-touch surfaces have a stable, flame-resistant barrier that stays cool and prevents contact?",
        type: "boolean",
        showIf: { key: "hasHotToTouchAppliances", equals: true },
        required: true,
      },
    ],
  },

  WA_AFH_078_BEDROOMS_USABLE_SPACE: {
    label: "Bedrooms Usable Space",
    reference: "WAC 388-76-10685, WAC 388-76-10690",
    description:
      "Confirm bedroom square footage, storage, light, furnishings, privacy, and room-sharing compliance.",
    icon: "ti-bed",
    questions: [
      {
        key: "allMeetSquareFootage",
        prompt:
          "Do all bedrooms meet the required usable space (80 sq ft single / 120 sq ft shared, excluding closets)?",
        type: "boolean",
        required: true,
      },
      {
        key: "allHaveClosets",
        prompt: "Does every bedroom have an accessible closet or wardrobe?",
        type: "boolean",
        required: true,
      },
      {
        key: "allHaveNaturalLight",
        prompt: "Is every bedroom an outside room with direct natural light?",
        type: "boolean",
        required: true,
      },
      {
        key: "bedsCompliant",
        prompt:
          "Are all beds at least 36 inches wide with clean bedding, and no upper bunks in use?",
        type: "boolean",
        required: true,
      },
      {
        key: "locksOfferedOrExempted",
        prompt:
          "Is a lock offered on every bedroom door, or is a documented safety exception on file for any that don't?",
        type: "boolean",
        required: true,
      },
      {
        key: "noMinorSharingWithNonRelative",
        prompt:
          "If any resident under 18 shares a bedroom, is that roommate their biological or legal parent/guardian — with no unrelated resident, staff, or household member sharing that room?",
        type: "boolean",
        required: true,
      },
      {
        key: "allDshsApproved",
        prompt:
          "Are all bedrooms currently DSHS-approved for resident placement?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_001_LICENSE_STATUS_AND_CONDITIONS: {
    label: "License Status and Conditions",
    reference: "WAC 388-76-10015; Ch. 70.128 RCW",
    description:
      "Confirm the home's AFH license is active and operating within its approved conditions.",
    icon: "ti-certificate",
    questions: [
      {
        key: "licenseActive",
        prompt:
          "Is the home's AFH license currently active and in good standing?",
        type: "boolean",
        required: true,
      },
      {
        key: "withinLicenseConditions",
        prompt:
          "Is the home operating fully within its approved license conditions (capacity, specialty designations, etc.)?",
        type: "boolean",
        required: true,
      },
      {
        key: "licenseExpirationDate",
        prompt: "When does the current license expire?",
        type: "date",
        required: true,
      },
    ],
  },

  WA_AFH_004_LIABILITY_INSURANCE: {
    label: "Liability Insurance",
    reference: "WAC 388-76-10191; WAC 388-76-10192",
    description: "Confirm required liability coverage is active with no lapse.",
    icon: "ti-shield-check",
    questions: [
      {
        key: "hasActiveCoverage",
        prompt:
          "Is required liability insurance currently active with no lapse in coverage?",
        type: "boolean",
        required: true,
      },
      {
        key: "meetsRequiredLimits",
        prompt: "Does the policy meet the required coverage limits?",
        type: "boolean",
        required: true,
      },
      {
        key: "policyExpirationDate",
        prompt: "When does the current policy expire?",
        type: "date",
        required: true,
      },
    ],
  },

  WA_AFH_023_SUCCESSION_PLAN: {
    label: "Succession Plan",
    reference: "WAC 388-76-10201",
    description:
      "Confirm a current succession plan is on file for continuity of home operations.",
    icon: "ti-replace",
    questions: [
      {
        key: "hasCurrentPlan",
        prompt: "Does the home have a current written succession plan?",
        type: "boolean",
        required: true,
      },
      {
        key: "reviewedRecently",
        prompt:
          "Has the plan been reviewed or updated within the last year, or since the last material change?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_024_MEDICAID_STATE_FUNDED_RESIDENT_OBLIGATIONS: {
    label: "Medicaid / State-Funded Resident Obligations",
    reference: "WAC 388-76-10205; WAC 388-76-10522",
    description:
      "Confirm required policies and notices for state-funded residents are current and applied.",
    icon: "ti-file-dollar",
    questions: [
      {
        key: "hasCurrentPolicy",
        prompt:
          "Does the home have a current written policy on accepting Medicaid/state-funded residents?",
        type: "boolean",
        required: true,
      },
      {
        key: "noticesProvided",
        prompt:
          "Have all applicable residents/representatives received the required notice?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_025_RESIDENT_FUNDS_PROTECTION: {
    label: "Resident Funds Protection",
    reference: "WAC 388-76-10215",
    description:
      "Confirm resident funds are protected with complete accounting and authorization records.",
    icon: "ti-cash",
    questions: [
      {
        key: "fundsProtected",
        prompt:
          "Are resident funds held/managed by the home protected according to policy?",
        type: "boolean",
        required: true,
      },
      {
        key: "recordsComplete",
        prompt:
          "Are accounting and authorization records for resident funds complete and up to date?",
        type: "boolean",
        required: true,
      },
      {
        key: "noUnexplainedDiscrepancies",
        prompt:
          "Are there zero unexplained discrepancies in resident fund accounts?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_040_RESIDENT_RECORD_AVAILABILITY_CONFIDENTIALITY: {
    label: "Resident Record Availability & Confidentiality",
    reference: "WAC 388-76-10315",
    description:
      "Confirm resident records are protected from unauthorized access while remaining available to authorized reviewers.",
    icon: "ti-clipboard-text",
    questions: [
      {
        key: "recordsSecured",
        prompt: "Are resident records protected from unauthorized access?",
        type: "boolean",
        required: true,
      },
      {
        key: "availableToAuthorizedStaff",
        prompt:
          "Are resident records readily available to authorized department staff upon request?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_041_RESIDENT_RECORD_RETENTION: {
    label: "Resident Record Retention",
    reference: "WAC 388-76-10315",
    description:
      "Confirm resident records are retained for the applicable regulatory period.",
    icon: "ti-archive",
    questions: [
      {
        key: "retentionPolicyFollowed",
        prompt:
          "Are resident records retained for the required regulatory retention period, including after discharge?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_052_MEDICATION_STORAGE_AND_SECURITY: {
    label: "Medication Storage and Security",
    reference: "Ch. 388-76 WAC — medication storage requirements",
    description:
      "Confirm medications are stored securely and identified according to requirements.",
    icon: "ti-lock",
    questions: [
      {
        key: "storedSecurely",
        prompt:
          "Are all medications stored securely with no unauthorized access?",
        type: "boolean",
        required: true,
      },
      {
        key: "storedPerInstructions",
        prompt:
          "Are medications stored according to labeling/manufacturer instructions (e.g., refrigeration where required)?",
        type: "boolean",
        required: true,
      },
      {
        key: "properlyIdentified",
        prompt:
          "Are all medications properly labeled and identified for the correct resident?",
        type: "boolean",
        required: true,
      },
    ],
  },
  WA_AFH_058_RESTRAINTS_INVOLUNTARY_SECLUSION: {
    label: "Restraints / Involuntary Seclusion",
    reference: "Ch. 388-76 WAC — restraints/seclusion",
    description:
      "Confirm no prohibited restraint or seclusion practices are in use.",
    icon: "ti-hand-stop",
    questions: [
      {
        key: "noProhibitedRestraints",
        prompt:
          "Are physical or mechanical restraints being used only in permitted, documented circumstances (never as a prohibited restraint)?",
        type: "boolean",
        required: true,
      },
      {
        key: "noChemicalRestraints",
        prompt:
          "Is medication used only for legitimate clinical purposes, never as a chemical restraint?",
        type: "boolean",
        required: true,
      },
      {
        key: "noInvoluntarySeclusion",
        prompt: "Is the home free of any involuntary seclusion practices?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_060_RESIDENT_RIGHTS_IMPLEMENTATION: {
    label: "Resident Rights Implementation",
    reference: "Ch. 388-76 WAC; Ch. 70.129 RCW",
    description:
      "Confirm resident rights are respected in daily operations, not just posted.",
    icon: "ti-users",
    questions: [
      {
        key: "dignityAndPrivacyRespected",
        prompt:
          "Are resident dignity and privacy respected in daily care and living arrangements?",
        type: "boolean",
        required: true,
      },
      {
        key: "participationAndCommunicationSupported",
        prompt:
          "Are residents supported in participation, communication, and receiving visitors as they choose?",
        type: "boolean",
        required: true,
      },
      {
        key: "noUnresolvedViolations",
        prompt:
          "Are there zero unresolved, substantiated resident rights violations?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_061_MEDICAID_PAYMENT_SOURCE_NOTICE_POLICY: {
    label: "Medicaid Payment-Source Notice / Policy",
    reference: "WAC 388-76-10522",
    description:
      "Confirm the Medicaid payment-source policy is current and provided to applicable residents.",
    icon: "ti-file-invoice",
    questions: [
      {
        key: "hasCurrentPolicy",
        prompt:
          "Does the home have a current written policy on accepting Medicaid as a payment source?",
        type: "boolean",
        required: true,
      },
      {
        key: "noticeProvidedToApplicable",
        prompt:
          "Have all applicable residents/representatives received the required notice of this policy?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_065_EMERGENCY_DISASTER_PLAN: {
    label: "Emergency / Disaster Plan",
    reference: "Ch. 388-76 WAC — emergency/disaster planning",
    description:
      "Confirm a current emergency/disaster plan is on file and staff are trained on it.",
    icon: "ti-alert-triangle",
    questions: [
      {
        key: "hasCurrentPlan",
        prompt:
          "Does the home have a current written emergency/disaster plan addressing resident needs and staff actions?",
        type: "boolean",
        required: true,
      },
      {
        key: "staffTrainedOnHire",
        prompt: "Are all staff trained on the plan when they begin work?",
        type: "boolean",
        required: true,
      },
      {
        key: "planReviewedRecently",
        prompt: "Has the plan been reviewed within the required timeframe?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_073_EVACUATION_ROUTES_EXITS: {
    label: "Evacuation Routes / Exits",
    reference: "Ch. 388-76 WAC — emergency/fire safety/doors",
    description:
      "Confirm designated evacuation routes and exits stay clear and usable at all times.",
    icon: "ti-door-exit",
    questions: [
      {
        key: "routesUnobstructed",
        prompt: "Are all designated evacuation routes free of obstructions?",
        type: "boolean",
        required: true,
      },
      {
        key: "exitsUsable",
        prompt:
          "Are all required exits usable and operable without special tools or knowledge?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_074_SAFETY_AND_MAINTENANCE: {
    label: "Safety and Maintenance",
    reference: "WAC 388-76-10750",
    description:
      "Confirm the physical plant, grounds, equipment, hazardous materials, and pest control are safe and well-maintained.",
    icon: "ti-tools",
    questions: [
      {
        key: "houseCleanAndSystemsOperable",
        prompt:
          "Are buildings, fixtures, and systems (heating, plumbing, electrical) clean, sanitary, operable, and in good repair?",
        type: "boolean",
        required: true,
      },
      {
        key: "groundsFreeOfHazards",
        prompt:
          "Are walkways, driveways, patios, and yards clear of trip/fall hazards, snow/ice, debris, or overgrowth?",
        type: "boolean",
        required: true,
      },
      {
        key: "equipmentAndFurnitureSafe",
        prompt:
          "Are resident furniture, appliances, mobility equipment, and mechanical systems structurally sound, stable, and in safe working order?",
        type: "boolean",
        required: true,
      },
      {
        key: "hazardousMaterialsSecured",
        prompt:
          "Are toxic substances, cleaning chemicals, insecticides, and flammable liquids stored safely and securely (locked where required by resident care assessments)?",
        type: "boolean",
        required: true,
      },
      {
        key: "garbageAndPestControlled",
        prompt:
          "Is trash stored in covered containers and removed regularly, with effective pest control measures in place?",
        type: "boolean",
        required: true,
      },
      {
        key: "hasUnresolvedHazard",
        prompt:
          "Is there a known physical safety hazard that hasn't been repaired yet?",
        type: "boolean",
      },
      {
        key: "hazardDetails",
        prompt:
          "Briefly describe the hazard and what's been done so far (required immediately under WAC — repairs must be initiated upon discovery).",
        type: "text",
        showIf: { key: "hasUnresolvedHazard", equals: true },
        required: true,
      },
    ],
  },

  WA_AFH_079_COMMON_USE_AREAS: {
    label: "Common-Use Areas",
    reference: "Ch. 388-76 WAC — common-use areas",
    description: "Confirm common-use areas are adequate, safe, and accessible.",
    icon: "ti-sofa",
    questions: [
      {
        key: "adequateSpaceProvided",
        prompt:
          "Does the home provide adequate common-use space for residents?",
        type: "boolean",
        required: true,
      },
      {
        key: "safeAndAccessible",
        prompt:
          "Are common-use areas maintained safely and accessible to all residents?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_082_EMERGENCY_FOOD_SUPPLY: {
    label: "Emergency Food Supply",
    reference: "Ch. 388-76 WAC — emergency food supply",
    description:
      "Confirm the home maintains at least 72 hours of on-site emergency food.",
    icon: "ti-shopping-cart",
    questions: [
      {
        key: "hasSeventyTwoHourSupply",
        prompt:
          "Does the home currently have at least 72 hours of on-site emergency food supply for residents and household members?",
        type: "boolean",
        required: true,
      },
      {
        key: "lastInventoryCheckDate",
        prompt: "When was the emergency food supply last checked/rotated?",
        type: "date",
        required: true,
      },
    ],
  },
  WA_AFH_053_MEDICATION_DISPOSAL_POLICY: {
    label: "Medication Disposal Policy",
    reference: "WAC 388-76-10490",
    description:
      "Confirm a current written medication disposal policy is in place and followed.",
    icon: "ti-trash-x",
    questions: [
      {
        key: "hasCurrentPolicy",
        prompt:
          "Does the home have a current written medication disposal policy?",
        type: "boolean",
        required: true,
      },
      {
        key: "disposalsDocumented",
        prompt:
          "Are medication disposal events documented as required by that policy?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_083_INFECTION_CONTROL_COMMUNICABLE_DISEASE: {
    label: "Infection Control / Communicable Disease",
    reference: "Ch. 388-76 WAC; WAC 388-76-10225(3)",
    description:
      "Confirm infection-prevention practices are followed and outbreak notification requirements are met.",
    icon: "ti-virus",
    questions: [
      {
        key: "infectionControlPracticesFollowed",
        prompt:
          "Are standard infection-control practices (handwashing, PPE, sanitation) consistently followed?",
        type: "boolean",
        required: true,
      },
      {
        key: "hasOutbreakEvent",
        prompt:
          "Has the home had a suspected food poisoning or communicable disease outbreak since the last check?",
        type: "boolean",
      },
      {
        key: "outbreakReported",
        prompt:
          "Was the local public health officer and department reporting system notified of that outbreak?",
        type: "boolean",
        showIf: { key: "hasOutbreakEvent", equals: true },
        required: true,
      },
    ],
  },

  WA_AFH_089_REQUIRED_POLICIES_CURRENT: {
    label: "Required Policies Current",
    reference: "Applicable provisions throughout Ch. 388-76 WAC",
    description:
      "Confirm all required written policies are present and current, as a general check across the home's policy set.",
    icon: "ti-file-check",
    questions: [
      {
        key: "allRequiredPoliciesPresent",
        prompt:
          "Does the home have every written policy required under Ch. 388-76 WAC currently on file?",
        type: "boolean",
        required: true,
      },
      {
        key: "allPoliciesCurrent",
        prompt:
          "Are all of those policies current, with review/effective dates up to date?",
        type: "boolean",
        required: true,
      },
      {
        key: "hasKnownGap",
        prompt: "Is any required policy known to be missing or outdated?",
        type: "boolean",
      },
      {
        key: "gapDetails",
        prompt: "Briefly describe which policy(ies) and what's outstanding.",
        type: "text",
        showIf: { key: "hasKnownGap", equals: true },
        required: true,
      },
    ],
  },

  WA_AFH_091_REQUIRED_POSTINGS_NOTICES: {
    label: "Required Postings / Notices",
    reference:
      "Applicable Ch. 388-76 WAC resident-rights/administrative provisions",
    description:
      "Confirm all required notices are posted, current, and visible.",
    icon: "ti-pin",
    questions: [
      {
        key: "allRequiredPostingsPresent",
        prompt:
          "Are all required postings (license, rights notices, inspection info, etc.) currently displayed?",
        type: "boolean",
        required: true,
      },
      {
        key: "postingsCurrentVersion",
        prompt: "Are the posted versions the current/up-to-date versions?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_092_INSPECTION_RESULTS_AVAILABILITY: {
    label: "Inspection Results Availability",
    reference: "Ch. 388-76 WAC resident rights; Ch. 70.129 RCW",
    description:
      "Confirm required inspection-related information is current and available as required.",
    icon: "ti-clipboard-list",
    questions: [
      {
        key: "inspectionResultsAvailable",
        prompt:
          "Are the home's most recent inspection results available for review as required?",
        type: "boolean",
        required: true,
      },
      {
        key: "keptCurrent",
        prompt:
          "Is this information kept current as new inspection results become available?",
        type: "boolean",
        required: true,
      },
    ],
  },
};
